import { apiService } from '@/services/apiService';
import type { TourId } from '@/components/TourGuide';

export interface TourPreference {
  version: string;
  completed: boolean;
  completedAt?: string | null;
}

interface UserPreferencesShape {
  tourGuides?: Partial<Record<TourId, TourPreference>>;
  /** When true, auto-show welcome modals on all pages are permanently disabled. */
  tourGuidesDismissed?: boolean;
  tourGuidesDismissedAt?: string;
  // Back-compat: older single flag
  postLoginTour?: TourPreference;
  [key: string]: unknown;
}

const DISMISSED_TOUR_PREFERENCE: TourPreference = {
  version: 'v1',
  completed: true,
};

const STORAGE_KEY = 'user_preferences';

function readLocal(): UserPreferencesShape | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as UserPreferencesShape;
    if (!parsed || typeof parsed !== 'object') return null;
    return parsed;
  } catch {
    return null;
  }
}

function writeLocal(next: UserPreferencesShape): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
  } catch {
    // ignore
  }
}

function normalizeTourGuides(data: unknown): UserPreferencesShape['tourGuides'] | null {
  const t = data as any;
  const candidate = t?.tourGuides ?? t?.data?.tourGuides ?? t?.preferences?.tourGuides ?? null;
  if (!candidate || typeof candidate !== 'object') return null;
  return candidate as UserPreferencesShape['tourGuides'];
}

function normalizeTourGuidesDismissed(data: unknown): boolean {
  const t = data as any;
  return Boolean(
    t?.tourGuidesDismissed ??
      t?.data?.tourGuidesDismissed ??
      t?.preferences?.tourGuidesDismissed,
  );
}

function getLocalTourPreference(tourId: TourId, local: UserPreferencesShape | null): TourPreference | null {
  if (!local) return null;
  if (local.tourGuidesDismissed) return DISMISSED_TOUR_PREFERENCE;
  return local.tourGuides?.[tourId] ?? (tourId === 'account_hub' ? local.postLoginTour ?? null : null);
}

function isTourPreferenceCompleted(pref: TourPreference | null | undefined, version: string): boolean {
  return pref?.version === version && pref.completed === true;
}

export const userPreferencesService = {
  isTourGuidesDismissedLocally(): boolean {
    return readLocal()?.tourGuidesDismissed === true;
  },

  dismissAllTourGuides(): void {
    const prev = readLocal() ?? {};
    const payload: UserPreferencesShape = {
      ...prev,
      tourGuidesDismissed: true,
      tourGuidesDismissedAt: new Date().toISOString(),
    };
    writeLocal(payload);

    void (async () => {
      try {
        await apiService.put<any>('/users/me/preferences', payload, true);
      } catch {
        // localStorage is the source of truth for dismiss
      }
    })();
  },

  async getTourPreference(tourId: TourId): Promise<TourPreference | null> {
    const local = readLocal();
    if (local?.tourGuidesDismissed) return DISMISSED_TOUR_PREFERENCE;

    const localPref = getLocalTourPreference(tourId, local);
    if (isTourPreferenceCompleted(localPref, localPref?.version ?? '')) return localPref;

    let remotePref: TourPreference | null = null;
    try {
      const resp = await apiService.get<any>('/users/me/preferences', true, true);
      if (resp.success) {
        if (normalizeTourGuidesDismissed(resp.data)) return DISMISSED_TOUR_PREFERENCE;

        const guides = normalizeTourGuides(resp.data);
        remotePref = guides?.[tourId] ?? null;

        if (!remotePref) {
          const maybeLegacy = (resp.data as any)?.postLoginTour ?? (resp.data as any)?.data?.postLoginTour ?? null;
          if (tourId === 'account_hub' && maybeLegacy && typeof maybeLegacy === 'object') {
            const version = String((maybeLegacy as any).version ?? '');
            if (version) {
              remotePref = {
                version,
                completed: Boolean((maybeLegacy as any).completed),
                completedAt: (maybeLegacy as any).completedAt ?? null,
              };
            }
          }
        }
      }
    } catch {
      // fall back to local
    }

    if (isTourPreferenceCompleted(remotePref, remotePref?.version ?? '')) return remotePref;
    if (isTourPreferenceCompleted(localPref, localPref?.version ?? '')) return localPref;
    return remotePref ?? localPref;
  },

  async setTourCompleted(input: { tourId: TourId; version: string }): Promise<void> {
    const prev = readLocal() ?? {};
    const nextGuides: UserPreferencesShape['tourGuides'] = {
      ...(prev.tourGuides ?? {}),
      [input.tourId]: {
        version: input.version,
        completed: true,
        completedAt: new Date().toISOString(),
      },
    };
    const payload: UserPreferencesShape = {
      ...prev,
      tourGuides: nextGuides,
    };

    writeLocal(payload);

    try {
      const resp = await apiService.put<any>('/users/me/preferences', payload, true);
      if (resp.success) return;
    } catch {
      // localStorage already updated
    }
  },
};

